import { useEffect, useMemo, useState } from "react";
import { CalendarDays, Check } from "lucide-react";
import { api } from "@/lib/api";

const ZONES = [
  ["America/New_York", "Eastern"], ["America/Chicago", "Central"], ["America/Denver", "Mountain"],
  ["America/Phoenix", "Arizona"], ["America/Los_Angeles", "Pacific"], ["America/Anchorage", "Alaska"], ["Pacific/Honolulu", "Hawaii"],
];
const detected = () => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return "America/Los_Angeles"; } };

export function SeasonDateForm({ token, compact = false, onInvalid }) {
  const [info, setInfo] = useState(undefined);
  const [day, setDay] = useState("");
  const [tz, setTz] = useState(detected());
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const zones = useMemo(() => (ZONES.some(([z]) => z === tz) ? ZONES : [[tz, tz.replace(/_/g, " ")], ...ZONES]), [tz]);

  useEffect(() => {
    api.get(`/coach-ready/season/${token}`).then(({ data }) => {
      setInfo(data);
      if (data.date) setDay(data.date);
      if (data.tz) setTz(data.tz);
    }).catch(() => { setInfo(null); onInvalid?.(); });
  }, [token, onInvalid]);

  const save = async (e) => {
    e.preventDefault();
    setState("busy"); setErr("");
    try {
      const { data } = await api.post(`/coach-ready/season/${token}`, { date: day, tz });
      setInfo(data); setState("saved");
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Couldn't save that. Try again."); setState("idle");
    }
  };

  if (!info) return null;
  return (
    <div data-testid="cr-season-form">
      <form onSubmit={save} className={compact ? "space-y-3" : "mt-8 space-y-5"}>
        <label className="block">
          <span className="kicker text-[#6E6E6E]">First practice</span>
          <div className="mt-2 flex items-center gap-3 border-2 border-[#1E1E1E] bg-white px-4 py-3">
            <CalendarDays size={20} className="text-[#DB9130] shrink-0" />
            <input type="date" required value={day} onChange={(e) => { setDay(e.target.value); setState("idle"); }}
              className="w-full bg-transparent text-lg text-[#1E1E1E] outline-none" data-testid="cr-season-date" />
          </div>
        </label>
        <label className="block">
          <span className="kicker text-[#6E6E6E]">Your time zone</span>
          <select value={tz} onChange={(e) => { setTz(e.target.value); setState("idle"); }} data-testid="cr-season-tz"
            className="mt-2 w-full border-2 border-[#D3CFC0] bg-white px-4 py-3 text-[#1E1E1E]">
            {zones.map(([z, label]) => <option key={z} value={z}>{label}</option>)}
          </select>
        </label>
        {err && <p className="text-sm text-[#B3261E]" data-testid="cr-season-error">{err}</p>}
        <button type="submit" disabled={!day || state === "busy"} data-testid="cr-season-save"
          className="w-full bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm px-6 py-3.5 hover:bg-[#D6922C] transition-colors disabled:opacity-60">
          {state === "busy" ? "Saving..." : info.date ? "Update my date" : "Save my date"}
        </button>
      </form>
      {(state === "saved" || (compact && info.date)) && (
        <div className="mt-4 border-2 border-[#1E1E1E] bg-white p-4 text-sm text-[#1E1E1E] flex gap-3" data-testid="cr-season-saved">
          <Check size={18} className="text-[#2E7D57] shrink-0 mt-0.5" />
          <span>{state === "saved" ? "Got it. " : ""}Your season starts the week of <b>{info.week1}</b>.{info.next_email && <> Your next weekly email arrives <b>{info.next_email}</b>.</>}</span>
        </div>
      )}
    </div>
  );
}

export const SeasonCard = ({ token }) => (
  <div className="border-2 border-[#D3CFC0] bg-[#F5F0EA] p-5" data-testid="cr-season-card">
    <span className="kicker" style={{ color: "#DB9130" }}>Your season</span>
    <h2 className="mt-1 font-head uppercase text-xl text-[#1E1E1E] leading-tight">When&apos;s your first practice?</h2>
    <p className="mt-2 mb-4 text-sm text-[#3A3A3A]">Every Monday of your season you&apos;ll get one short coaching email, timed to where you are. Change it anytime.</p>
    <SeasonDateForm token={token} compact />
  </div>
);
